import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import adminService from "../../api/services/adminService";
import PaginationBar from "../../components/common/PaginationBar";
import { TableSkeleton } from "../../components/common/skeletons";

function paymentLabel(row) {
  const pm = String(row.payment_method || "").toUpperCase();
  if (pm.includes("MANUAL") || pm.includes("TRANSFER") || pm.includes("BANK")) {
    return "Manual Payment";
  }
  if (pm.includes("APP_STORE") || pm.includes("PLAY_STORE") || pm.includes("CARD")) {
    return "Card";
  }
  if (row.source === "manual") return "Manual Payment";
  if (row.source === "revenuecat") return "Card";
  return pm || "—";
}

function userLabel(row) {
  if (row.account_deleted_at || !row.user_id) {
    return (
      <div>
        <p className="font-semibold text-gray-900">
          {row.deleted_user_name || "Deleted user"}
        </p>
        <p className="text-xs text-red-600">Account deleted</p>
        <p className="text-xs text-gray-500">
          {row.deleted_user_email || row.deleted_user_phone || `ID ${row.deleted_user_id || "—"}`}
        </p>
      </div>
    );
  }
  return (
    <div>
      <p className="font-semibold text-gray-900">
        {[row.firstName, row.lastName].filter(Boolean).join(" ") || "—"}
      </p>
      <p className="text-xs text-gray-500">{row.email || row.contactNo || "—"}</p>
    </div>
  );
}

export default function MonthlySubscriptionReport() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminService.getMonthlySubscriptionReport({
        year,
        month,
        page,
        limit: 50,
      });
      setData(res);
    } catch (e) {
      const msg =
        (typeof e?.response?.data?.message === "string" && e.response.data.message) ||
        (typeof e?.message === "string" && e.message) ||
        (typeof e?.response?.data?.error === "string" && e.response.data.error) ||
        "Failed to load report";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [year, month, page]);

  useEffect(() => {
    load();
  }, [load]);

  const summary = data?.monthSummary;
  const rows = data?.subscriptions || [];
  const pagination = data?.pagination;

  function exportCsv() {
    if (!rows.length) {
      toast.error("No rows to export");
      return;
    }
    const header = [
      "Purchase date",
      "User",
      "Email/Phone",
      "Plan",
      "Duration",
      "Amount",
      "Currency",
      "Payment method",
      "Status",
      "Account deleted",
    ];
    const lines = rows.map((r) =>
      [
        r.start_date || "",
        r.account_deleted_at
          ? r.deleted_user_name || ""
          : [r.firstName, r.lastName].filter(Boolean).join(" "),
        r.account_deleted_at
          ? r.deleted_user_email || r.deleted_user_phone || ""
          : r.email || r.contactNo || "",
        r.subscription_name || "",
        r.duration || r.duration_days || "",
        r.amount ?? "",
        r.currency || "",
        paymentLabel(r),
        r.record_state || r.status || "",
        r.account_deleted_at ? "yes" : "no",
      ]
        .map((v) => `"${String(v).replace(/"/g, '""')}"`)
        .join(",")
    );
    const blob = new Blob([[header.join(","), ...lines].join("\n")], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `subscriptions-${year}-${String(month).padStart(2, "0")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-rose-100 bg-gradient-to-br from-rose-50 to-white p-5">
        <h2 className="text-xl font-black text-gray-900">Monthly subscription report</h2>
        <p className="text-sm text-gray-600 mt-1 max-w-3xl">
          Month-wise purchase ledger with plan, duration, amount, currency, and payment
          method. Records remain after card removal or account deletion (identity snapshot).
        </p>
        <div className="mt-4 flex flex-wrap gap-3 items-end">
          <div>
            <label className="text-xs font-semibold text-gray-600">Year</label>
            <input
              type="number"
              className="block border border-gray-300 rounded-lg px-3 py-2 text-sm"
              value={year}
              onChange={(e) => {
                setPage(1);
                setYear(Number(e.target.value) || now.getFullYear());
              }}
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600">Month</label>
            <select
              className="block border border-gray-300 rounded-lg px-3 py-2 text-sm"
              value={month}
              onChange={(e) => {
                setPage(1);
                setMonth(Number(e.target.value));
              }}
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <option key={m} value={m}>
                  {new Date(2000, m - 1, 1).toLocaleString("en", { month: "long" })}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            onClick={exportCsv}
            className="rounded-xl bg-[#FB565F] text-white text-sm font-bold px-4 py-2.5"
          >
            Export CSV
          </button>
        </div>
      </div>

      {loading ? (
        <TableSkeleton rows={6} />
      ) : error ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          {error}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="rounded-2xl border bg-white p-4 shadow-sm">
              <p className="text-xs uppercase font-semibold text-gray-500">Subscriptions</p>
              <p className="text-3xl font-black tabular-nums mt-1">
                {summary?.subscriptionCount ?? 0}
              </p>
            </div>
            {Object.entries(summary?.byPaymentMethod || {}).map(([k, v]) => (
              <div key={k} className="rounded-2xl border bg-white p-4 shadow-sm">
                <p className="text-xs uppercase font-semibold text-gray-500">{k}</p>
                <p className="text-3xl font-black tabular-nums mt-1">{v}</p>
              </div>
            ))}
          </div>

          <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-gray-500">Purchase date</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-500">User</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-500">Plan</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-500">Duration</th>
                  <th className="px-3 py-2 text-right font-semibold text-gray-500">Amount</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-500">Method</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-500">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t">
                    <td className="px-3 py-2 whitespace-nowrap">
                      {r.start_date
                        ? new Date(r.start_date).toLocaleDateString()
                        : "—"}
                    </td>
                    <td className="px-3 py-2">{userLabel(r)}</td>
                    <td className="px-3 py-2">{r.subscription_name || "—"}</td>
                    <td className="px-3 py-2">
                      {r.duration || (r.duration_days ? `${r.duration_days}d` : "—")}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold">
                      {r.amount != null ? Number(r.amount).toLocaleString() : "—"}{" "}
                      <span className="text-gray-500 text-xs">{r.currency || ""}</span>
                    </td>
                    <td className="px-3 py-2">{paymentLabel(r)}</td>
                    <td className="px-3 py-2 capitalize">{r.record_state || r.status || "—"}</td>
                  </tr>
                ))}
                {!rows.length ? (
                  <tr>
                    <td colSpan={7} className="px-3 py-12 text-center text-gray-400">
                      No subscriptions in this month
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          {pagination ? (
            <PaginationBar
              page={pagination.currentPage}
              totalPages={pagination.totalPages}
              totalItems={pagination.totalItems}
              pageSize={pagination.itemsPerPage}
              label="records"
              onPageChange={setPage}
            />
          ) : null}
        </>
      )}
    </div>
  );
}
